import { mapFilterUndefined } from "@glideapps/ts-necessities";
import { definedMap } from "collection-utils";
import values from "lodash/values";
import type { Doc } from "../../../common/docUrl";
import type { LocalizedStringKey } from "../../components/localization";
import type { SerializedComponent } from "../../components/SerializedApp";
import type { QuotaBannerRequirements, ValueProducer } from "../../components/types";
import { getFeatureSetting } from "../../feature-settings";
import { handlerForActionKind, rewriteActions } from "../actions";
import { transformArray } from "../builder-utils";
import type {
    ActionPropertyDescriptor,
    ActionsRecord,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentSpecialCaseDescriptor,
    EditedColumnsAndTables,
    PropertyDescriptor,
    PropertyTableGetter,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
    WireInflationBackend,
    WireRowComponentInflationBackend,
    WireTableComponentHydrator,
    WireValueGetter,
} from "../components";
import {
    combineEditedColumnsAndTables,
    convertEditedColumnsToIndirect,
    mapActionsRecord,
    NumberPropertyStyle,
    RequiredKind,
    shouldUseInputContextForTransform,
} from "../components";
import { PopulationMode } from "../components/description-handlers";
import { getEditedColumnsForProperties, rewriteFilter } from "../components/descriptor-utils";
import { populateDescription } from "../components/populate-description";
import { getDefaultPrimitiveActionKinds } from "../components/primitive";
import { SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import {
    getColumnProperty,
    getFilterProperty,
    makeFilterProperty,
    makeSwitchProperty,
} from "../components/property-utils";
import type {
    ArrayContentDescription,
    ArrayScreenFormat,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
    TransformableContentDescription,
} from "../description";
import {
    ActionKind,
    ArrayTransformKind,
    getArrayTransforms,
    getPrimitiveNonHiddenColumns,
    getTableColumn,
    makeInputOutputTables,
    PropertyKind,
} from "../description";
import { getActionsRecord, getEmailArrayFilter } from "../description-utils";
import { handlerForComponentKind } from "../handlers";
import type { SerializedElementBuilder } from "../serialized-component";

export interface InlineListBuild {
    readonly tables: InputOutputTables;
    readonly mutatingScreenKind: MutatingScreenKind | undefined;
    readonly inputContext: SerializedComponent;
    readonly outputContext: SerializedComponent | undefined;
}

export interface FixedArrayContent {
    readonly properties: ArrayContentDescription;
    readonly format: ArrayScreenFormat;
}

export interface ArrayContentHandler<TContentDesc extends ArrayContentDescription> {
    readonly format: ArrayScreenFormat;
    readonly helpPath: Doc;

    readonly quotaBannerRequirements: QuotaBannerRequirements;
    readonly needsOutputContext: boolean;
    readonly sourcePropertyLabel?: readonly [string, PropertySection];
    readonly isEditor: boolean;
    readonly defaultIsTable: boolean;
    readonly supportsEmptyArrays: boolean;
    readonly supportsDynamicFilter: boolean;
    readonly hasComponents: boolean;
    // `undefined` means no paging
    readonly defaultPageSize: number | undefined;

    getActions(desc: TContentDesc, schema: SchemaInspector | undefined): ActionsRecord;

    getNeedsOrder(desc: TContentDesc): boolean;

    processArray(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        doSort: boolean
    ): SerializedComponent;
    makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        caption: SerializedComponent | undefined,
        // For array screens with components, this is the VP which to set to
        // the current row, so we can display it in the left panel.
        arrayScreenRow: SerializedComponent | undefined
    ): SerializedComponent;

    defaultContentDescription<TBase>(
        baseDesc: TBase,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        insideInlineList: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        getIndirectTable: PropertyTableGetter<TContentDesc> | undefined
    ): (TBase & TContentDesc) | undefined;

    adjustContentDescriptionAfterUpdate(
        desc: TContentDesc,
        updates: Partial<TContentDesc> | undefined,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): TContentDesc;

    getContentPropertyDescriptors<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: TransformableContentDescription | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        array: ValueProducer | undefined
    ): ReadonlyArray<PropertyDescriptor>;
    getActionDescriptors<T extends TContentDesc>(
        desc: T,
        getPropertyTable: PropertyTableGetter<T> | undefined,
        schema: SchemaInspector | undefined
    ): readonly ActionPropertyDescriptor<T>[];

    getSpecialCaseDescriptor?(ccc: AppDescriptionContext): ComponentSpecialCaseDescriptor;

    // Does not include screens used by actions
    getScreensUsed(
        desc: TContentDesc,
        tables: InputOutputTables | undefined,
        insideInlineList: boolean
    ): readonly string[];

    needsCaption(desc: TContentDesc | undefined): PropertySection | undefined;

    needValidation(desc: TContentDesc): boolean;
    makeValidationFlag(
        b: SerializedElementBuilder,
        desc: TContentDesc,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined;

    getEditedColumns<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        desc: T,
        // These must be the array's tables, not the containing screen's
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        insideInlineList: boolean,
        isDefaultArrayScreen: boolean,
        withActions: boolean
    ): EditedColumnsAndTables;

    getDefaultValues(
        builder: ComponentBuilder,
        desc: TContentDesc,
        tables: InputOutputTables
    ): readonly [string, SerializedComponent][];

    // Returns the properties to be searched just via the content, not taking
    // into account the detail screen or anything else.
    getBasicSearchProperties(desc: TContentDesc): readonly string[];

    getDescriptiveName(
        _desc: TContentDesc,
        _caption: string | undefined,
        inlineListHostTable: TableGlideType | undefined
    ): [string, string] | undefined;

    fixContentDescription(desc: TContentDesc): FixedArrayContent | undefined;

    rewriteContentAfterReload<T extends TContentDesc>(
        desc: T,
        // These are the tables of the array
        tables: InputOutputTables,
        getPropertyDescriptors: (desc: T) => readonly PropertyDescriptor[],
        // These are the tables of the properties from `getPropertyDescriptors`, which,
        // in the case of an Inline List, will be different from the tables of the array,
        // because they'll be the tables of the containing screen.
        propertyTables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isRewrite: boolean
    ): T | undefined;

    inflateContent<T extends TContentDesc>(
        b: WireInflationBackend,
        desc: T,
        captionGetter: WireValueGetter | undefined,
        containingRowIB: WireRowComponentInflationBackend | undefined,
        componentID: string | undefined
    ): WireTableComponentHydrator | undefined;
}

const reversePropertyHandler = new SwitchPropertyHandler({ reverse: false }, "Reverse list", PropertySection.Sort);

export function propertyDescriptorsForTransforms<T extends TransformableContentDescription>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    withOrder: boolean,
    withLimit: boolean,
    withContainingScreen: boolean
): readonly PropertyDescriptor[] {
    const descriptors: PropertyDescriptor[] = [
        {
            kind: PropertyKind.Transforms,
            property: { name: "transforms" },
            // FIXME: We don't actually use this label
            label: "Filter",
            getIndirectTable: getPropertyTable,
            section: PropertySection.FilterData,
            addText: "Add Filter",
            description: "Limit the items displayed based on their properties.",
            allowContextTable: true,
            allowLHSUserProfileColumns: false,
            withContainingScreen: withContainingScreen,
        },
    ];

    if (withLimit) {
        descriptors.push({
            // ##limitTransformInGlobalSearch:
            // NOTE: If we ever make this configurable as a column, we will
            // have to walk this property description in the app walker!
            kind: PropertyKind.Number,
            property: {
                id: "limit",
                get: (d: T) => {
                    if (d.transforms === undefined) return undefined;
                    return mapFilterUndefined(d.transforms, t =>
                        t.kind === ArrayTransformKind.Limit ? t.numRows : undefined
                    )[0];
                },
                update: (d: T, v: PropertyDescription | undefined) => {
                    const transforms = d.transforms?.filter(t => t.kind !== ArrayTransformKind.Limit) ?? [];
                    if (v !== undefined) {
                        transforms.push({ kind: ArrayTransformKind.Limit, numRows: v });
                    }
                    return { transforms };
                },
            },
            label: "Limit number of items",
            placeholder: "10",
            defaultValue: 10,
            section: PropertySection.Options,
            style: NumberPropertyStyle.Stepper,
            required: RequiredKind.NotRequiredDefaultMissing,
        });
    }
    if (withOrder) {
        descriptors.push({
            kind: PropertyKind.Sorts,
            property: { name: "transforms" },
            // FIXME: We don't actually use this label
            label: "Sort",
            getIndirectTable: getPropertyTable,
            section: PropertySection.Sort,
        });
    }
    return descriptors;
}

export function makeEmptyMessage(
    b: SerializedElementBuilder,
    searchActiveFlag: SerializedComponent | undefined,
    inlineListBuild: InlineListBuild | undefined,
    defaultMessage?: LocalizedStringKey
): SerializedComponent | undefined {
    // FIXME: Shouldn't we return the default message when no search active
    // flag is there?
    if (searchActiveFlag === undefined || inlineListBuild !== undefined) return undefined;
    return b.if(
        searchActiveFlag,
        b.localizedString("noResultsFound"),
        b.localizedString(defaultMessage ?? "thereAreNoItemsYet")
    );
}

export function getGroupByColumn(
    desc: ArrayContentDescription,
    table: TableGlideType,
    supportGrouping: boolean
): TableColumn | undefined {
    if (!supportGrouping) return undefined;
    const groupByColumnName = getColumnProperty(desc.groupByColumn);
    if (groupByColumnName === undefined) return undefined;
    return getTableColumn(table, groupByColumnName);
}

export abstract class ArrayContentHandlerBase<TContentDesc extends ArrayContentDescription>
    implements ArrayContentHandler<TContentDesc>
{
    constructor(public readonly format: ArrayScreenFormat) {}

    public get helpPath(): Doc {
        return "inlineList";
    }

    public get quotaBannerRequirements(): QuotaBannerRequirements {
        return { needListQuota: true, needMapQuota: false };
    }

    protected get canReverse(): boolean {
        return true;
    }

    public getNeedsOrder(_desc: TContentDesc | undefined): boolean {
        return true;
    }

    public get needsOutputContext(): boolean {
        return false;
    }

    public get isEditor(): boolean {
        return false;
    }

    public get defaultIsTable(): boolean {
        return false;
    }

    protected get supportsGrouping(): boolean {
        return false;
    }

    protected get supportsLimit(): boolean {
        return true;
    }

    public get supportsDynamicFilter(): boolean {
        return false;
    }

    public get supportsEmptyArrays(): boolean {
        return false;
    }

    public get hasComponents(): boolean {
        return false;
    }

    public get defaultPageSize(): number | undefined {
        return undefined;
    }

    public getActions(desc: TContentDesc, schema: SchemaInspector | undefined): ActionsRecord {
        // NOTE: We pass `undefined` for the indirect table here, assuming
        // that the actions we get aren't dependent on that.
        const descrs = this.getActionDescriptors(desc, undefined, schema);
        if (descrs === undefined) return { actions: [] };

        return getActionsRecord(desc, descrs);
    }

    public abstract makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        caption: SerializedComponent | undefined,
        arrayScreenRow: SerializedComponent | undefined
    ): SerializedComponent;

    public processArray(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        doSort: boolean
    ): SerializedComponent {
        const filter = getFilterProperty(desc.filter);
        const reverse = this.canReverse && reversePropertyHandler.getSwitch(desc);
        let containingScreenTable: TableGlideType | undefined;
        let containingScreenContext: SerializedComponent | undefined;
        // Inline lists won't give us an output context if the inline list
        // itself doesn't use it, so we use the input context as a fallback.
        // https://github.com/quicktype/glide/issues/8515
        if (
            shouldUseInputContextForTransform(inlineListBuild?.mutatingScreenKind) ||
            inlineListBuild?.outputContext === undefined
        ) {
            containingScreenTable = inlineListBuild?.tables.input;
            containingScreenContext = inlineListBuild?.inputContext;
        } else {
            containingScreenTable = inlineListBuild?.tables.output;
            containingScreenContext = inlineListBuild?.outputContext;
        }

        return transformArray(
            builder,
            table,
            arrayData,
            containingScreenTable,
            containingScreenContext,
            filter,
            reverse,
            getArrayTransforms(desc, doSort && this.getNeedsOrder(desc), this.supportsLimit),
            uniqueID
        );
    }

    public defaultContentDescription<TBase>(
        baseDesc: TBase,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        insideInlineList: boolean,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        getIndirectTable: PropertyTableGetter<TContentDesc> | undefined
    ): (TBase & TContentDesc) | undefined {
        const desc: TBase & ArrayContentDescription = {
            ...baseDesc,
            actions: [{ kind: ActionKind.PushDetailScreen }],
            components: [],
            reverse: makeSwitchProperty(false),
            groupByColumn: undefined,
            transforms: [],
            limit: undefined,
        };
        return populateDescription(
            d =>
                this.getContentPropertyDescriptors(
                    getIndirectTable,
                    insideInlineList,
                    d,
                    ccc,
                    mutatingScreenKind,
                    isDefaultArrayScreen,
                    false,
                    undefined
                ),
            PopulationMode.Default,
            desc as TBase & TContentDesc,
            desc as TBase & TContentDesc,
            tables,
            ccc,
            false,
            undefined
        );
    }

    public adjustContentDescriptionAfterUpdate(
        desc: TContentDesc,
        _updates: Partial<TContentDesc> | undefined,
        _tables: InputOutputTables,
        _ccc: AppDescriptionContext
    ): TContentDesc {
        return desc;
    }

    public getContentPropertyDescriptors<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        _ccc: AppDescriptionContext,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        _array: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const descrs: PropertyDescriptor[] = [];

        if (desc !== undefined && getEmailArrayFilter(desc) !== undefined) {
            descrs.push({
                kind: PropertyKind.Filter,
                property: { name: "filter" },
                // FIXME: We don't actually use this label
                label: "Data filter",
                getIndirectTable: getPropertyTable,
                section: PropertySection.FilterByUser,
            });
        }

        if (withTransforms) {
            descrs.push(
                ...propertyDescriptorsForTransforms(
                    getPropertyTable,
                    definedMap(desc, d => this.getNeedsOrder(d)) ?? true,
                    this.supportsLimit,
                    insideInlineList
                )
            );
        }

        if (this.supportsGrouping && getFeatureSetting("grouping")) {
            descrs.push({
                kind: PropertyKind.Column,
                property: { name: "groupByColumn" },
                label: "Group by",
                required: false,
                editable: true,
                searchable: false,
                emptyByDefault: true,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                section: PropertySection.GroupBy,
            });
        }

        return descrs;
    }

    public getActionDescriptors<T extends TContentDesc>(
        _desc: T,
        getPropertyTable: PropertyTableGetter<T> | undefined,
        schema: SchemaInspector | undefined
    ): readonly ActionPropertyDescriptor<T>[] {
        return [
            {
                kind: PropertyKind.Action,
                property: { name: "actions" },
                label: "Action",
                kinds: getDefaultPrimitiveActionKinds(schema, undefined),
                defaultAction: true,
                defaultActionForUndefined: true,
                required: false,
                getIndirectTable: getPropertyTable,
                section: PropertySection.ImportantAction,
            },
        ];
    }

    public getScreensUsed(
        _desc: TContentDesc,
        _tables: InputOutputTables | undefined,
        _insideInlineList: boolean
    ): readonly string[] {
        return [];
    }

    public needsCaption(_desc: TContentDesc | undefined): PropertySection | undefined {
        return PropertySection.Title;
    }

    public getEditedColumns<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        insideInlineList: boolean,
        isDefaultArrayScreen: boolean,
        withActions: boolean
    ): EditedColumnsAndTables {
        const descriptors = this.getContentPropertyDescriptors(
            getPropertyTable,
            insideInlineList,
            desc,
            ccc,
            mutatingScreenKind,
            isDefaultArrayScreen,
            false,
            undefined
        );

        let editedColumns = getEditedColumnsForProperties(
            descriptors,
            desc,
            tables,
            mutatingScreenKind,
            ccc,
            withActions
        );

        if (withActions) {
            let tablesForActions: InputOutputTables | undefined = tables;
            if (getPropertyTable !== undefined) {
                tablesForActions = definedMap(getPropertyTable(tables, desc, ccc), makeInputOutputTables);
            }

            if (tablesForActions !== undefined) {
                for (const actions of values(this.getActions(desc, ccc))) {
                    for (const action of actions) {
                        const handler = handlerForActionKind(action.kind);
                        if (handler === undefined) continue;

                        let editedInAction = handler.getEditedColumns(
                            action,
                            tablesForActions,
                            ccc,
                            mutatingScreenKind
                        );
                        if (getPropertyTable !== undefined) {
                            editedInAction = {
                                editedColumns: convertEditedColumnsToIndirect(editedInAction?.editedColumns ?? []),
                                deletedTables: editedInAction?.deletedTables ?? [],
                            };
                        }

                        editedColumns = combineEditedColumnsAndTables(editedColumns, editedInAction);
                    }
                }
            }
        }

        return editedColumns;
    }

    public getDefaultValues(
        _builder: ComponentBuilder,
        _desc: TContentDesc,
        _tables: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return [];
    }

    public abstract getBasicSearchProperties(desc: TContentDesc): readonly string[];

    public needValidation(_desc: TContentDesc): boolean {
        return false;
    }

    public makeValidationFlag(
        _b: SerializedElementBuilder,
        _desc: TContentDesc,
        _outputContext: SerializedComponent,
        _table: TableGlideType
    ): SerializedComponent | undefined {
        return undefined;
    }

    public getDescriptiveName(
        _desc: TContentDesc,
        _caption: string | undefined,
        _inlineListHostTable: TableGlideType | undefined
    ): [string, string] | undefined {
        return undefined;
    }

    public fixContentDescription(_desc: TContentDesc): FixedArrayContent | undefined {
        return undefined;
    }

    public rewriteContentAfterReload<T extends TContentDesc>(
        desc: T,
        contentTables: InputOutputTables,
        getPropertyDescriptors: (desc: T) => readonly PropertyDescriptor[],
        propertyTables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isRewrite: boolean
    ): T | undefined {
        desc = {
            ...desc,
            filter: definedMap(getFilterProperty(desc.filter), f =>
                makeFilterProperty(rewriteFilter(f, contentTables.input))
            ),
        };
        desc = populateDescription(
            getPropertyDescriptors,
            PopulationMode.Rewrite,
            desc,
            desc,
            propertyTables,
            ccc,
            true,
            mutatingScreenKind
        );
        desc = {
            ...desc,
            ...mapActionsRecord(
                this.getActions(desc, ccc),
                actions => rewriteActions(actions, contentTables, ccc, mutatingScreenKind) ?? []
            ),
        };
        if (this.hasComponents) {
            const components = mapFilterUndefined(desc.components ?? [], cd =>
                handlerForComponentKind(cd.kind)?.rewriteAfterReload(
                    cd,
                    contentTables,
                    ccc,
                    mutatingScreenKind,
                    isRewrite
                )
            );
            desc = { ...desc, components };
        } else {
            // We keep components around as a courtesy, to make switching between
            // array and detail screens non-disruptive, but we don't rewrite them
            // because then you might get "hidden" screens and actions clogging up
            // the app description.  That means when we do rewrite we need to
            // remove the components, or else switching back to them will produce
            // dangling references.
            desc = { ...desc, components: undefined };
        }
        return desc;
    }

    public inflateContent<T extends TContentDesc>(
        _ib: WireInflationBackend,
        _desc: T,
        _captionGetter: WireValueGetter | undefined,
        _containingRowIB: WireRowComponentInflationBackend | undefined,
        _componentID: string | undefined
    ): WireTableComponentHydrator | undefined {
        return undefined;
    }
}
